/**
 * The landing page: the price calculator, the logo preview, the
 * quote-by-email box, the sticky "See my price" bar.
 *
 * Ported from components/landing/PriceCalculator.tsx (state and the
 * panel), CupPreview.tsx (the drawn cup and the dropped logo),
 * QuoteByEmail.tsx (the form and its three states), StickyBar.tsx
 * (the scroll rule), lib/products.ts (penceLabel), lib/format.ts (gbp),
 * lib/dispatch.ts (the proof and dispatch dates, the same small function
 * product.js carries) and lib/analytics.ts (generate_lead).
 *
 * The markup is sections/landing-hero.liquid, which renders the panel for
 * the default choice and a JSON script ([data-calc-json]) with every cup
 * variant in the cups collection: handle, size, wall, cups, price in
 * pence, lead_time, artwork_fee, badge, mix (and each product's rungs, in
 * its Quantity option's order). This module reads it and owns the panel
 * from then on: size tiles, wall tiles, the quantity tiles (the rungs the
 * wall has, a badge's highlight on its tile), the per-cup and total, the
 * dates, the Order link (/products/<handle>?qty=<n>&wall=<w>) and the
 * summary line in the quote box. Nothing is fetched for a price.
 *
 * The quote box is a Shopify contact form. It is posted with fetch so the
 * page does not reload; Shopify's bot challenge (a redirect to /challenge)
 * falls back to a native submit so the buyer can pass it.
 *
 * The WhatsApp and phone taps are reported by snippets/contact-buttons.
 */

const EN_GB = new Intl.NumberFormat("en-GB");

/* The prices in the JSON are Shopify's, in the visitor's currency (GBP on
   this store; setCurrency() follows Markets if another one is ever on). */
let CURRENCY = "GBP";
let MONEY = new Intl.NumberFormat("en-GB", { style: "currency", currency: "GBP" });
let MONEY_FINE = MONEY;

function setCurrency(code) {
  const c = typeof code === "string" && /^[A-Z]{3}$/.test(code) ? code : "GBP";
  if (c === CURRENCY) return;
  try {
    MONEY = new Intl.NumberFormat("en-GB", { style: "currency", currency: c });
    MONEY_FINE = new Intl.NumberFormat("en-GB", {
      style: "currency",
      currency: c,
      minimumFractionDigits: 3,
      maximumFractionDigits: 3,
    });
    CURRENCY = c;
  } catch {
    /* unknown code: stay on GBP */
  }
}

/** "10.0p": pence a cup to one decimal (lib/products.ts penceLabel). */
function penceLabel(perCup) {
  if (CURRENCY !== "GBP") return MONEY_FINE.format(Number(perCup));
  return `${(Number(perCup) * 100).toFixed(1)}p`;
}

/** Pounds (a float) to "£1,234.56" (lib/format.ts gbp). */
function gbp(n) {
  return MONEY.format(n);
}

/* ── lib/dispatch.ts (the same function as product.js) ───────────────────── */

const PROOF_CUTOFF_HOUR = 14;
const TZ = "Europe/London";
const LEAD_WORKING_DAYS = { "4 to 5 working days": 5, "2 to 3 weeks": 15 };

/* Lead time text to whole working days, the upper end of its window:
   "N to M working days" -> M, "N to M weeks" -> M x 5. The map above is the
   fallback for text that does not parse. */
function leadWorkingDays(leadTime) {
  const t = String(leadTime ?? "").toLowerCase();
  const m = t.match(/(\d+)(?:\s*(?:to|-|\u2013)\s*(\d+))?\s*(working\s+days?|days?|weeks?)/);
  if (m) {
    const upper = Number(m[2] ?? m[1]);
    return /week/.test(m[3]) ? upper * 5 : upper;
  }
  // Text with no number: 15 if it mentions weeks, else 5 (as snippets/lead-working-days.liquid).
  return LEAD_WORKING_DAYS[leadTime] ?? (/week/.test(t) ? 15 : 5);
}

function londonParts(d) {
  const f = new Intl.DateTimeFormat("en-GB", {
    timeZone: TZ,
    hour: "numeric",
    hourCycle: "h23",
    weekday: "short",
  });
  const parts = Object.fromEntries(f.formatToParts(d).map((p) => [p.type, p.value]));
  return { hour: Number(parts.hour), weekday: parts.weekday };
}

function isWeekend(d) {
  const { weekday } = londonParts(d);
  return weekday === "Sat" || weekday === "Sun";
}

/** Noon (UTC) on the London calendar date of `d`: 12:00 GMT or 13:00 BST, so
 *  stepping whole days never crosses midnight when the clocks change. */
function londonNoon(d) {
  const f = new Intl.DateTimeFormat("en-GB", { timeZone: TZ, year: "numeric", month: "numeric", day: "numeric" });
  const p = Object.fromEntries(f.formatToParts(d).map((x) => [x.type, x.value]));
  return new Date(Date.UTC(Number(p.year), Number(p.month) - 1, Number(p.day), 12));
}

function addWorkingDays(from, days) {
  const d = londonNoon(from);
  let left = days;
  while (left > 0) {
    d.setUTCDate(d.getUTCDate() + 1);
    if (!isWeekend(d)) left--;
  }
  return d;
}

/**
 *   ordered before 2pm   proof the next working day
 *   ordered after 2pm    proof the working day after that
 *   dispatch             the proof day plus the cup's lead time in working
 *                        days (the longer end of its window: 5 for "4 to 5
 *                        working days", 15 for "2 to 3 weeks")
 */
export function dispatchEstimate(leadTime, now = new Date()) {
  const { hour } = londonParts(now);
  const beforeCutoff = !isWeekend(now) && hour < PROOF_CUTOFF_HOUR;
  const proofBy = addWorkingDays(now, beforeCutoff ? 1 : 2);
  const lead = leadWorkingDays(leadTime);
  const dispatchBy = addWorkingDays(proofBy, lead);
  return { beforeCutoff, proofBy, dispatchBy };
}

/** "Fri 3 Oct" */
export function shortDate(d) {
  return new Intl.DateTimeFormat("en-GB", {
    timeZone: TZ,
    weekday: "short",
    day: "numeric",
    month: "short",
  }).format(d);
}

/* ── lib/analytics.ts ────────────────────────────────────────────────────── */

function track(name, params = {}) {
  const g = typeof window !== "undefined" ? window.gtag : null;
  if (typeof g !== "function") return;
  g("event", name, { currency: CURRENCY, ...params });
}

/* ── the price panel ─────────────────────────────────────────────────────── */

const WALL_LABEL = { double: "Double wall, no sleeve needed", single: "Single wall" };
const SIZE_RE = /^(4|6|8|10|12|16|20)oz$/;

function setupCalculator(root) {
  const jsonEl = root.querySelector("[data-calc-json]");
  if (!jsonEl) return;
  let data;
  try {
    data = JSON.parse(jsonEl.textContent);
  } catch {
    return;
  }
  const products = Array.isArray(data.products) ? data.products : [];
  if (products.length === 0) return;
  const context = data.context || root.dataset.calcContext || "landing";
  const productPath = data.productPath || "/products/";
  setCurrency(data.currency);

  const sizesEl = root.querySelector("[data-calc-sizes]");
  const wallLabel = root.querySelector("[data-calc-wall-label]");
  const wallsEl = root.querySelector("[data-calc-walls]");
  const qtysEl = root.querySelector("[data-calc-qtys]");
  const perEl = root.querySelector("[data-calc-per]");
  const totalEl = root.querySelector("[data-calc-total]");
  const subEl = root.querySelector("[data-calc-sub]");
  const proofEl = root.querySelector("[data-calc-proof]");
  const proofLine = root.querySelector("[data-calc-proof-line]");
  const leadRow = root.querySelector("[data-calc-lead-row]");
  const leadEl = root.querySelector("[data-calc-lead]");
  const leadLine = root.querySelector("[data-calc-lead-line]");
  const cta = root.querySelector("[data-calc-cta]");
  const summaryEl = root.querySelector("[data-quote-summary]");
  const bodyInput = root.querySelector("[data-quote-body]");
  const cupCanvas = root.querySelector("[data-cup-canvas]");

  /* The state the component kept: a size, a wall and a quantity. The wall
     survives a switch to a size that only comes single, so coming back to
     8oz lands on double again; the quantity survives a switch to a wall
     without it (2,500 is double wall only), so coming back lands on it. */
  let size = root.dataset.calcSize || products[0].size;
  let wall = root.dataset.calcWall === "single" ? "single" : "double";
  let qty = Number(root.dataset.calcQty) || 2500;

  // ?size=12oz&wall=single&qty=5000: where the ad sent the visitor. A wall
  // or rung the size does not have falls back as a click would (wallFor,
  // variantFor: the first wall, the nearest rung).
  try {
    const params = new URLSearchParams(window.location.search);
    const wanted = params.get("size") || "";
    if (SIZE_RE.test(wanted) && products.some((p) => p.size === wanted)) size = wanted;
    const wantedWall = params.get("wall");
    if (wantedWall === "single" || wantedWall === "double") wall = wantedWall;
    const wantedQty = Number(params.get("qty"));
    if (Number.isInteger(wantedQty) && wantedQty >= 1000 && wantedQty <= 1000000) qty = wantedQty;
  } catch {
    /* no URL access: keep the Liquid default */
  }

  const productFor = (s) => products.find((p) => p.size === s) || products[0];

  /** The walls a product comes in, in variant order (single then double). */
  function wallsOf(p) {
    const seen = [];
    for (const v of p.variants) if (!seen.includes(v.wall)) seen.push(v.wall);
    return seen;
  }

  /** lib/products variantFor: the wall asked for, else the first wall. */
  function wallFor(p) {
    const walls = wallsOf(p);
    return walls.includes(wall) ? wall : walls[0];
  }

  /** A wall's variants, the quantity tiles it shows, by cups (not the
      Quantity option's order: Shopify lists the double wall's 2,500 after
      every single wall rung there). As the Liquid draws them. */
  function ladderOf(p, w) {
    return p.variants
      .filter((v) => v.wall === w && Number(v.cups) > 0 && !v.mix)
      .sort((a, b) => Number(a.cups) - Number(b.cups));
  }

  /** The variant for a wall at a rung; the nearest rung the wall has when
      the exact one is missing (a tie goes to the smaller), as the Liquid. */
  function variantFor(p, w, n) {
    let best = null;
    let bestDiff = Infinity;
    for (const v of ladderOf(p, w)) {
      const diff = Math.abs(Number(v.cups) - n);
      if (diff < bestDiff || (diff === bestDiff && Number(v.cups) < Number(best.cups))) {
        best = v;
        bestDiff = diff;
      }
    }
    return best || p.variants[0];
  }

  /** The highlight on a rung's tile: its badge, then " · Save N%", its
      price a cup against the same wall's first rung (the smallest that is
      not mix-only), from the prices; left out under 1%. "" for no badge. */
  function badgeText(p, v) {
    const badge = typeof v.badge === "string" ? v.badge.trim() : "";
    if (!badge) return "";
    const first = ladderOf(p, v.wall)
      .filter((x) => !x.mix)
      .reduce((a, x) => (a && Number(a.cups) <= Number(x.cups) ? a : x), null);
    if (!first || !Number(first.price) || !Number(v.cups)) return badge;
    const save = Math.round((1 - Number(v.price) / Number(v.cups) / (Number(first.price) / Number(first.cups))) * 100);
    return save >= 1 ? `${badge} · Save ${save}%` : badge;
  }

  function el(tag, className, text) {
    const node = document.createElement(tag);
    if (className) node.className = className;
    if (text !== undefined) node.textContent = text;
    return node;
  }

  function renderWalls(p, w) {
    if (!wallsEl) return;
    const walls = wallsOf(p);
    const show = walls.length > 1;
    if (wallLabel) wallLabel.hidden = !show;
    // site.css gives .calc__walls display:flex, which beats [hidden]: use both.
    wallsEl.hidden = !show;
    wallsEl.style.display = show ? "" : "none";
    const key = show ? walls.join() : "";
    if (wallsEl.dataset.calcKey === key) {
      // the same tiles: toggle them in place, so a focused radio keeps focus
      syncTiles(wallsEl, "[data-calc-wall-tile]", (t) => t.dataset.calcWallTile === w);
      return;
    }
    const refocus = focusedValue(wallsEl, "calcWallInput");
    wallsEl.dataset.calcKey = key;
    wallsEl.textContent = "";
    if (!show) return;
    for (const key of walls) {
      const label = el("label", `calc__wall${key === w ? " is-on" : ""}`);
      label.dataset.calcWallTile = key;
      const input = document.createElement("input");
      input.type = "radio";
      input.name = "wall";
      input.value = key;
      input.dataset.calcWallInput = key;
      input.checked = key === w;
      label.appendChild(input);
      label.appendChild(document.createTextNode(WALL_LABEL[key] || key));
      wallsEl.appendChild(label);
    }
    restoreFocus(wallsEl, "calc-wall-input", refocus);
  }

  /** Mark the tile that is on (is-on and the radio's checked). */
  function syncTiles(container, selector, isOn) {
    for (const tile of container.querySelectorAll(selector)) {
      const on = isOn(tile);
      tile.classList.toggle("is-on", on);
      const input = tile.querySelector("input");
      if (input && input.checked !== on) input.checked = on;
    }
  }

  /** The value of the radio in this group that has focus, if one has. */
  function focusedValue(container, dataKey) {
    const a = document.activeElement;
    return a instanceof HTMLInputElement && container.contains(a) ? a.dataset[dataKey] : undefined;
  }

  function restoreFocus(container, attr, value) {
    if (value === undefined) return;
    const input =
      container.querySelector(`[data-${attr}="${CSS.escape(value)}"]`) ||
      container.querySelector(`input:checked`);
    if (input) input.focus({ preventScroll: true });
  }

  function renderQtys(p, w, n) {
    if (!qtysEl) return;
    // Only the rungs this wall has, each with its highlight (or "").
    const list = ladderOf(p, w).map((v) => ({ cups: Number(v.cups), badge: badgeText(p, v) }));
    qtysEl.classList.toggle("calc__qtys--badges", p.variants.some((v) => typeof v.badge === "string" && v.badge.trim()));
    const key = list.map((t) => `${t.cups}:${t.badge}`).join();
    if (qtysEl.dataset.calcKey === key) {
      syncTiles(qtysEl, "[data-calc-qty-tile]", (t) => Number(t.dataset.calcQtyTile) === n);
      return;
    }
    const refocus = focusedValue(qtysEl, "calcQtyInput");
    qtysEl.dataset.calcKey = key;
    qtysEl.textContent = "";
    for (const { cups: rung, badge } of list) {
      const label = el("label", `calc__qty${badge ? " sh-calc-qty--badge" : ""}${rung === n ? " is-on" : ""}`);
      label.dataset.calcQtyTile = String(rung);
      const input = document.createElement("input");
      input.type = "radio";
      input.name = "qty";
      input.value = String(rung);
      input.dataset.calcQtyInput = String(rung);
      input.checked = rung === n;
      label.appendChild(input);
      label.appendChild(document.createTextNode(EN_GB.format(rung)));
      // after the number in the DOM, so the radio reads "2,500 Best value · Save 5%";
      // the stylesheet lifts it to the tile's top edge
      if (badge) {
        label.appendChild(document.createTextNode(" "));
        label.appendChild(el("span", "sh-calc-qty__badge", badge));
      }
      qtysEl.appendChild(label);
    }
    restoreFocus(qtysEl, "calc-qty-input", refocus);
  }

  function render() {
    const p = productFor(size);
    const w = wallFor(p);
    const v = variantFor(p, w, qty);
    if (!v) return;

    // the size tiles
    if (sizesEl) {
      for (const tile of sizesEl.querySelectorAll("[data-calc-size-tile]")) {
        const on = tile.dataset.calcSizeTile === p.handle;
        tile.classList.toggle("is-on", on);
        const input = tile.querySelector("input");
        if (input) input.checked = on;
      }
    }
    renderWalls(p, w);
    renderQtys(p, w, Number(v.cups));

    // the quote: the variant price is the cups ex VAT; the big total adds
    // the one-time artwork setup, as the product page's Total does
    // (product.js: subtotal + fee), so the price does not jump after the
    // click. The price a cup stays the cups alone.
    const cups = Number(v.cups) || 1;
    const subtotal = Number(v.price) / 100;
    const perCup = subtotal / cups;
    const fee = Number(v.artwork_fee) || 0;
    const total = subtotal + fee;

    if (perEl) perEl.textContent = `${penceLabel(perCup)} a cup`;
    if (totalEl) totalEl.textContent = gbp(total);
    if (subEl) {
      subEl.textContent =
        `ex VAT for ${EN_GB.format(cups)} cups` +
        (fee ? `, includes ${gbp(fee)} one-time artwork setup` : " · artwork set up free");
    }
    const href = `${productPath}${p.handle}?qty=${cups}&wall=${v.wall}`;
    root.dataset.calcOrderTotal = gbp(total);
    root.dataset.calcOrderHref = href;
    root.dispatchEvent(new CustomEvent("calc:change", { bubbles: true, detail: { total: gbp(total), href } }));
    // The chosen size's lead time (its own erapack.lead_time) and its
    // minimum (the smallest run on sale, mix-only runs left out); the
    // delivery price is in the basket and at checkout.
    const lead = typeof v.lead_shown === "string" ? v.lead_shown.trim() : "";
    const runs = (p.variants || [])
      .filter((x) => !x.mix && Number(x.price) > 0 && Number(x.cups) > 0)
      .map((x) => Number(x.cups));
    const minCups = runs.length ? Math.min(...runs) : 0;
    if (leadRow) leadRow.hidden = !lead;
    if (leadEl && lead) leadEl.textContent = `Lead time ${lead}`;
    if (leadLine) {
      leadLine.textContent =
        (minCups ? `Minimum order ${EN_GB.format(minCups)} cups. ` : "") +
        "Counted from the day you approve the proof.";
    }
    if (proofEl) proofEl.textContent = "Free proof the same day";
    if (proofLine) proofLine.textContent = "Approve the free proof, nothing prints until you say so.";
    if (cta) cta.setAttribute("href", href);
    if (cupCanvas) cupCanvas.setAttribute("aria-label", `A ${p.size} cup with your logo on it`);

    const summary =
      `${EN_GB.format(cups)} × ${p.size} ${w} wall at ${penceLabel(perCup)} a cup, ${gbp(subtotal)} ex VAT` +
      (fee ? ` plus ${gbp(fee)} artwork setup` : "");
    if (summaryEl) summaryEl.textContent = summary;
    if (bodyInput) bodyInput.value = `Please email me this price: ${summary}. (Sent from ${context}.)`;
    root.dataset.calcSummary = summary;
  }

  root.addEventListener("change", (e) => {
    const t = e.target;
    if (!(t instanceof HTMLInputElement)) return;
    if (t.dataset.calcSizeInput !== undefined) {
      const p = products.find((x) => x.handle === t.dataset.calcSizeInput);
      if (p) size = p.size;
    } else if (t.dataset.calcWallInput !== undefined) {
      wall = t.dataset.calcWallInput === "single" ? "single" : "double";
    } else if (t.dataset.calcQtyInput !== undefined) {
      qty = Number(t.dataset.calcQtyInput) || qty;
    } else {
      return;
    }
    render();
  });

  render();
}

/* ── the logo preview (components/landing/CupPreview.tsx) ────────────────── */

/** lib CupPreview shade(): lighten (amount > 0) or darken a hex colour. */
function shade(hex, amount) {
  const m = hex.replace("#", "");
  const n = parseInt(m.length === 3 ? m.replace(/./g, "$&$&") : m, 16);
  const ch = (v) => Math.max(0, Math.min(255, Math.round(v + (amount < 0 ? v : 255 - v) * amount)));
  return `rgb(${ch((n >> 16) & 255)},${ch((n >> 8) & 255)},${ch(n & 255)})`;
}

/**
 * "See your cup": drop a logo and it is drawn on a plain cup, wrapped round
 * the body the way print sits on the real thing. Nothing is uploaded or
 * saved; it all happens in the browser. The cup is drawn, not
 * photographed, so it reads as "your logo on a cup" and never as a
 * finished product shot or someone else's cup.
 */
function setupCupPreview(box) {
  const stage = box.querySelector("[data-cup-stage]");
  const canvas = box.querySelector("[data-cup-canvas]");
  const hint = box.querySelector("[data-cup-hint]");
  const fileInput = box.querySelector("[data-cup-file]");
  const note = box.querySelector("[data-cup-note]");
  const reset = box.querySelector("[data-cup-reset]");
  if (!stage || !canvas) return;
  const colour = "#ffffff";
  let logo = null;
  let drawn = 0;

  function paint() {
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    const run = ++drawn;
    const W = canvas.width;
    const H = canvas.height;
    ctx.clearRect(0, 0, W, H);

    // Cup geometry: a tapered body with an elliptical rim and base.
    const topW = W * 0.62;
    const botW = W * 0.46;
    const top = H * 0.16;
    const bot = H * 0.9;
    const cx = W / 2;
    const ry = 0.13;
    const body = new Path2D();
    body.moveTo(cx - topW / 2, top);
    body.lineTo(cx - botW / 2, bot);
    body.ellipse(cx, bot, botW / 2, (botW / 2) * ry, 0, Math.PI, 0, true);
    body.lineTo(cx + topW / 2, top);
    body.ellipse(cx, top, topW / 2, (topW / 2) * ry, 0, 0, Math.PI, false);
    body.closePath();

    // Shadow on the table.
    ctx.save();
    ctx.fillStyle = "rgba(0,0,0,0.10)";
    ctx.filter = "blur(10px)";
    ctx.beginPath();
    ctx.ellipse(cx + 6, bot + 4, botW * 0.62, botW * 0.11, 0, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();

    // Body in the chosen colour, lit from the left.
    const g = ctx.createLinearGradient(cx - topW / 2, 0, cx + topW / 2, 0);
    g.addColorStop(0, shade(colour, -0.1));
    g.addColorStop(0.28, shade(colour, 0.06));
    g.addColorStop(0.62, colour);
    g.addColorStop(1, shade(colour, -0.2));
    ctx.fillStyle = g;
    ctx.fill(body);

    const finish = () => {
      // Rim and the sleeve line.
      ctx.globalAlpha = 1;
      ctx.lineWidth = 3;
      ctx.strokeStyle = "rgba(0,0,0,0.12)";
      ctx.beginPath();
      ctx.ellipse(cx, top, topW / 2, (topW / 2) * ry, 0, 0, Math.PI * 2);
      ctx.stroke();
      ctx.fillStyle = "#ffffff";
      ctx.beginPath();
      ctx.ellipse(cx, top, topW / 2 - 3, (topW / 2) * ry - 2, 0, 0, Math.PI * 2);
      ctx.fill();
      ctx.fillStyle = "rgba(0,0,0,0.06)";
      ctx.beginPath();
      ctx.ellipse(cx, top, topW / 2 - 9, (topW / 2) * ry - 5, 0, 0, Math.PI * 2);
      ctx.fill();
    };

    // Logo wrapped across the body: drawn in vertical strips, each squeezed
    // by the cosine of its angle round the cylinder.
    if (!logo) {
      finish();
      return;
    }
    const img = new Image();
    img.onload = () => {
      if (run !== drawn) return;
      const bandTop = top + (bot - top) * 0.3;
      const bandH = (bot - top) * 0.36;
      const lh = Math.min(bandH, img.height * ((topW * 0.62) / img.width));
      const strips = 96;
      const arc = Math.PI * 0.72; // the angle the logo spans round the cup
      const radius = topW * 0.46;
      const xAt = (i) => cx + Math.sin((i / strips - 0.5) * arc) * radius;
      ctx.save();
      ctx.clip(body);
      // Printed on white: ink multiplies with the cup, so a logo's white
      // background disappears instead of sitting on it as a pale box.
      ctx.globalCompositeOperation = "multiply";
      for (let i = 0; i < strips; i++) {
        const theta = ((i + 0.5) / strips - 0.5) * arc;
        const x0 = xAt(i);
        const x1 = xAt(i + 1);
        const y = bandTop + (bandH - lh) / 2 + Math.abs(theta) * 6;
        ctx.globalAlpha = 1 - Math.abs(theta) * 0.22;
        ctx.drawImage(img, (i / strips) * img.width, 0, img.width / strips, img.height, x0, y, x1 - x0 + 0.75, lh);
      }
      ctx.restore();
      finish();
    };
    img.src = logo;
  }

  function setLogo(dataUrl) {
    logo = dataUrl;
    // .cup-preview__hint is display:grid, which beats [hidden]: use both.
    if (hint) {
      hint.hidden = !!logo;
      hint.style.display = logo ? "none" : "";
    }
    if (note) note.hidden = !logo;
    if (!logo && fileInput) fileInput.value = "";
    paint();
  }

  function read(file) {
    if (!file || !/^image\//.test(file.type)) return;
    const r = new FileReader();
    r.onload = () => setLogo(String(r.result));
    r.readAsDataURL(file);
  }

  stage.addEventListener("dragover", (e) => {
    e.preventDefault();
    stage.classList.add("is-over");
  });
  stage.addEventListener("dragleave", () => stage.classList.remove("is-over"));
  stage.addEventListener("drop", (e) => {
    e.preventDefault();
    stage.classList.remove("is-over");
    read(e.dataTransfer && e.dataTransfer.files[0]);
  });
  if (fileInput) {
    fileInput.addEventListener("change", () => read(fileInput.files && fileInput.files[0]));
  }
  if (reset) reset.addEventListener("click", () => setLogo(null));

  paint();
}

/* ── the quote box (components/landing/QuoteByEmail.tsx) ─────────────────── */

const QUOTE_EMAIL_KEY = "erapack:quote-email";
/* Set before a challenged post goes native: the reload that shows "Sent."
   reports the lead once, then forgets it. */
const QUOTE_LEAD_KEY = "erapack:quote-lead";

function sentBox(email) {
  const box = document.createElement("div");
  box.className = "quote-box quote-box--sent";
  box.setAttribute("role", "status");
  box.dataset.quoteSent = "";
  const strong = document.createElement("strong");
  strong.textContent = "Sent.";
  box.appendChild(strong);
  box.appendChild(
    document.createTextNode(
      ` Your price is on its way to ${email}. One of the team will follow up if you have questions.`,
    ),
  );
  return box;
}

/**
 * A native post has already landed (form.posted_successfully?): fill in
 * the address remembered before the fallback submit.
 */
function fillSentBox(box) {
  try {
    const lead = window.sessionStorage.getItem(QUOTE_LEAD_KEY);
    if (lead !== null) {
      window.sessionStorage.removeItem(QUOTE_LEAD_KEY);
      track("generate_lead", { context: lead || "landing", value: 0 });
    }
  } catch {
    /* storage blocked: the lead goes unreported */
  }
  const sentEmail = box.querySelector("[data-quote-email]");
  if (!sentEmail) return;
  try {
    const remembered = window.sessionStorage.getItem(QUOTE_EMAIL_KEY);
    if (remembered) {
      sentEmail.textContent = remembered;
      window.sessionStorage.removeItem(QUOTE_EMAIL_KEY);
    }
  } catch {
    /* storage blocked: "you" stays */
  }
}

function setupQuoteBox(form) {
  const button = form.querySelector("[data-quote-submit]");
  const error = form.querySelector("[data-quote-error]");
  const emailInput = form.querySelector("[data-quote-email-input]");
  const context = form.dataset.quoteContext || "landing";
  let state = "idle";

  const setSending = (on) => {
    state = on ? "sending" : "idle";
    if (button) {
      button.disabled = on;
      button.textContent = on ? "Sending…" : "Email me this price";
    }
  };

  form.addEventListener("submit", async (e) => {
    e.preventDefault();
    if (state === "sending") return;
    if (typeof form.reportValidity === "function" && !form.reportValidity()) return;
    const email = emailInput ? emailInput.value.trim() : "";
    if (error) error.hidden = true;
    setSending(true);
    try {
      const res = await fetch(form.getAttribute("action") || "/contact", {
        method: "POST",
        body: new FormData(form),
        headers: { Accept: "text/html,application/xhtml+xml" },
        credentials: "same-origin",
        redirect: "follow",
      });
      if (/\/challenge/.test(res.url || "")) {
        // Shopify wants a bot check: let the browser post the form and show it.
        try {
          window.sessionStorage.setItem(QUOTE_EMAIL_KEY, email);
          window.sessionStorage.setItem(QUOTE_LEAD_KEY, context);
        } catch {
          /* fine */
        }
        setSending(false);
        state = "sending";
        form.submit();
        return;
      }
      // Shopify answers a post it took with a redirect to ?contact_posted=true;
      // anything else (a 200 page with form.errors, a 4xx) did not send.
      let posted = false;
      try {
        posted = new URL(res.url, window.location.href).searchParams.get("contact_posted") === "true";
      } catch {
        posted = false;
      }
      if (!res.ok || !posted) throw new Error(String(res.status));
      track("generate_lead", { context, value: 0 });
      state = "sent";
      form.replaceWith(sentBox(email));
    } catch {
      setSending(false);
      state = "error";
      if (error) error.hidden = false;
    }
  });
}

/* ── the sticky bar (components/landing/StickyBar.tsx) ───────────────────── */

/*
 * With a price panel on the page the bar is the order button itself: the
 * live total and the same link as "Order these cups", shown on a phone
 * whenever the panel's own button is not (mostly) on screen, from the
 * first screen, where it sits below the size and quantity tiles, and
 * hidden while it is in view. A hidden bar is inert, so its link cannot take focus. Without a
 * panel it keeps the old rule: shown after scrolling data-sticky-after px.
 */
function setupStickyBar(bar) {
  const link = bar.querySelector("a");
  const setShown = (show) => {
    bar.classList.toggle("is-shown", show);
    bar.setAttribute("aria-hidden", show ? "false" : "true");
    bar.inert = !show;
  };

  const calc = document.querySelector("[data-calc]");
  const result = calc && calc.querySelector(".calc__result");
  const cta = calc && calc.querySelector("[data-calc-cta]");
  if (!calc || !result || !link || typeof IntersectionObserver !== "function") {
    const after = Number(bar.dataset.stickyAfter) || 560;
    const onScroll = () => setShown(window.scrollY > after);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return;
  }

  const label = (cta && cta.textContent.trim()) || link.textContent.trim();
  const sync = () => {
    const total = calc.dataset.calcOrderTotal;
    const href = calc.dataset.calcOrderHref || (cta && cta.getAttribute("href"));
    if (href) link.setAttribute("href", href);
    link.textContent = "";
    if (total) {
      const price = document.createElement("span");
      price.className = "sticky-bar__price";
      price.textContent = `${total} ex VAT`;
      link.appendChild(price);
      const dot = document.createElement("span");
      dot.setAttribute("aria-hidden", "true");
      dot.textContent = "·";
      link.appendChild(dot);
    }
    link.appendChild(document.createElement("span")).textContent = label;
  };
  sync();
  calc.addEventListener("calc:change", sync);

  setShown(false);
  new IntersectionObserver(
    (entries) => {
      for (const entry of entries) setShown(!(entry.isIntersecting && entry.intersectionRatio >= 0.6));
    },
    { threshold: [0, 0.6, 1] },
  ).observe(cta || result);
}

/* ── boot ────────────────────────────────────────────────────────────────── */

function wire(scope) {
  scope.querySelectorAll("[data-calc]").forEach(setupCalculator);
  scope.querySelectorAll("[data-cup-preview]").forEach(setupCupPreview);
  scope.querySelectorAll("[data-quote-form]").forEach(setupQuoteBox);
  scope.querySelectorAll("[data-quote-sent]").forEach(fillSentBox);
  scope.querySelectorAll("[data-sticky-bar]").forEach(setupStickyBar);
}

function init() {
  wire(document);
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", init, { once: true });
} else {
  init();
}

// The theme editor re-renders a section in place: wire the new markup.
document.addEventListener("shopify:section:load", (e) => {
  wire(e.target && e.target.querySelectorAll ? e.target : document);
});
